import { FC } from 'react'
import { useFormik } from 'formik'
import { useT } from '_metronic/i18n/Metronici18n'
import { Button } from '../../../../../components/Button/Button'
import { FormTextArea } from '../../../../../components/Form/FormTextArea'
import { IRadioOption, Radio } from '../../../../../components/Input/Radio'
import { ErrorLabel } from '../../../../../components/Label/ErrorLabel'
import { LoadingIndicator } from '../../../../../components/Loading/LoadingIndicator'
import { useModal } from '../../../../../components/Modal/ModalContext'
import { TriagemRiscoBadge } from '../../../../../components/TriagemRisco/TriagemRiscoBadge'
import { ButtonType } from '../../../../../../_metronic/helpers/enumsUI'
import { showError } from '../../../../../../_metronic/helpers/AlertHelper'
import { CorTriagem, RISCOS } from '../../../../../components/TriagemRisco/riscoTriagem'
import { TriagemHistorico } from '../../../triagem/core/_models'
import { registrarDecisaoTriagem } from '../../../triagem/core/_requests'
import { decisaoTriagemSchema } from './validation/decisaoTriagemSchema'

type AlterarClassificacaoTriagemModalProps = {
  idTriagem: number
  corEfetiva?: CorTriagem
  onDecisao: (historico: TriagemHistorico) => void
}

const CORES = Object.keys(RISCOS) as CorTriagem[]

/**
 * Override da classificação pelo médico.
 *
 * Qualquer uma das quatro cores é escolhível, inclusive menos grave que a atual, e salvar grava
 * direto — sem segundo diálogo de confirmação ao rebaixar (decisão do épico). O que o produto
 * exige em troca é o motivo, e ele é obrigatório pelo schema, pelo botão desabilitado e pelo
 * backend.
 */
export const AlterarClassificacaoTriagemModal: FC<AlterarClassificacaoTriagemModalProps> = ({
  idTriagem,
  corEfetiva,
  onDecisao,
}) => {
  const { closeModal } = useModal()
  const { t } = useT()

  const formik = useFormik<{ cor: string; motivo: string }>({
    validationSchema: decisaoTriagemSchema(t, corEfetiva),
    initialValues: { cor: '', motivo: '' },
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true)
      try {
        const historico = await registrarDecisaoTriagem(idTriagem, {
          acao: 'ALTERAR',
          cor: values.cor as CorTriagem,
          motivo: values.motivo,
        })
        onDecisao(historico)
        closeModal()
      } catch (error) {
        showError(error)
      } finally {
        setSubmitting(false)
      }
    },
  })

  const opcoesCor: IRadioOption<CorTriagem>[] = CORES.map((cor) => {
    const atual = cor === corEfetiva
    return {
      id: cor,
      value: cor,
      disabled: atual,
      descricao: (
        <span className='d-flex align-items-center gap-2'>
          <TriagemRiscoBadge cor={cor} />
          {RISCOS[cor].nome}
          {atual && <span className='text-muted'>{t('triagem.decisao.atual')}</span>}
        </span>
      ),
    }
  })

  return (
    <form onSubmit={formik.handleSubmit}>
      <fieldset className='mb-4'>
        <legend className='fs-6 fw-bold'>{t('triagem.decisao.nova_classificacao')}</legend>
        <Radio<CorTriagem>
          id='cor'
          isColumn
          options={opcoesCor}
          value={formik.values.cor as CorTriagem}
          disabled={formik.isSubmitting}
          onChange={(cor) => {
            formik.setFieldTouched('cor', true, false)
            formik.setFieldValue('cor', cor)
          }}
        />
        {formik.touched.cor && formik.errors.cor && <ErrorLabel>{formik.errors.cor}</ErrorLabel>}
      </fieldset>

      <FormTextArea
        className='mb-4'
        name='motivo'
        label={t('triagem.decisao.motivo_label')}
        formik={formik}
        rows={3}
        isRequired
      />

      <div className='d-flex justify-content-end gap-2'>
        <Button className='btn-secondary' onClick={closeModal}>
          {t('common.cancel', undefined, 'Cancelar')}
        </Button>
        <Button
          type={ButtonType.Submit}
          className='btn-primary'
          disabled={!formik.isValid || !formik.dirty || formik.isSubmitting}
        >
          {formik.isSubmitting ? <LoadingIndicator /> : t('triagem.decisao.salvar')}
        </Button>
      </div>
    </form>
  )
}
